import React, { useState, useEffect } from 'react';
import { Calendar, User, MapPin, Upload, Save, AlertCircle, CheckCircle, X, Search, RefreshCw, Landmark } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

export default function GeneralObjectionForm({ onBack }) {
  const { language, translations } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  const [context, setContext] = useState({
    projectId: null,
    proposalId: null,
    distName: '',
    talukaName: '',
    villageName: ''
  });

  const [formData, setFormData] = useState({
    gateNo: '',
    objectionDate: new Date().toISOString().split('T')[0],
    objectorName: '',
    objectorAddress: '',
    description: ''
  });

  const [owners, setOwners] = useState([]);
  const [ownerMessage, setOwnerMessage] = useState("");
  const [selectedOwnerId, setSelectedOwnerId] = useState("");

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  useEffect(() => {
    const projectData = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    const proposalId = localStorage.getItem('currentProposalId');
    const villageContext = JSON.parse(localStorage.getItem('selectedVillageForObjection') || '{}');

    setContext({
      projectId: projectData.id || projectData.project_id,
      proposalId: proposalId,
      distName: villageContext.dist_name || '',
      talukaName: villageContext.taluka_name || '',
      villageName: villageContext.village_name || ''
    });
  }, []);

  useEffect(() => {
    const fetchOwners = async () => {
      if (formData.gateNo && context.projectId && context.proposalId && context.villageName) {
        try {
          const res = await apiService.getVerifyOwnerSurveyNo(formData.gateNo, context.projectId, context.proposalId);
          if (res?.status === 200 && res?.verify && Array.isArray(res.data)) {
            const filteredOwners = res.data.filter(owner => 
                owner.village_name === context.villageName || owner.v_name === context.villageName
            );
            if (filteredOwners.length > 0) {
              setOwners(filteredOwners);
              setOwnerMessage("");
            } else {
              setOwners([]);
              setOwnerMessage(`${translations[language]?.notFoundIn || 'Not found in'} ${context.villageName}`);
            }
          } else {
            setOwners([]);
            setOwnerMessage(translations[language]?.noDataFound || "No data");
          }
        } catch (err) {
          setOwners([]);
        }
      }
    };
    const timer = setTimeout(fetchOwners, 500);
    return () => clearTimeout(timer);
  }, [formData.gateNo, context.villageName]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'gateNo') { setOwners([]); setSelectedOwnerId(""); }
  };

  const handleOwnerSelect = (e) => {
    const ownerId = e.target.value;
    setSelectedOwnerId(ownerId);
    const owner = owners.find(o => o.land_owner_id.toString() === ownerId);
    if (owner) {
      setFormData(prev => ({
        ...prev,
        objectorName: owner.owner_name || "",
        objectorAddress: owner.owner_address || ""
      }));
    }
  };

  const handleSave = async () => {
    if (!formData.gateNo || !formData.objectorName || !selectedOwnerId) {
      showNotification(translations[language]?.fillRequiredFields || 'Details missing', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const apiFormData = new FormData();
      apiFormData.append('project_id', context.projectId);
      apiFormData.append('proposal_id', context.proposalId);
      apiFormData.append('village_name', context.villageName);
      apiFormData.append('affected_survey_no', formData.gateNo);
      apiFormData.append('objection_date', formData.objectionDate);
      apiFormData.append('objector_name', formData.objectorName);
      apiFormData.append('objector_address', formData.objectorAddress);
      apiFormData.append('objection_summary', formData.description || 'General objection');
      apiFormData.append('OBJECTION_STATUS', 'Pending');
      if (selectedFile) apiFormData.append('supporting_document_file', selectedFile);

      await apiService.postAddGeneralObjection(apiFormData);
      showNotification(translations[language]?.SuccessfullySaved || 'Saved!', 'success');
      setTimeout(() => onBack(), 1000);
    } catch (error) {
      showNotification('Error saving data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 animate-fadeIn">
      {/* Toast Notification */}
      {notification.show && (
        <div className={`fixed top-6 right-6 p-4 rounded-lg shadow-2xl z-[100] border-l-4 ${notification.type === 'success' ? 'bg-green-50 border-green-500 text-green-800' : 'bg-red-50 border-red-500 text-red-800'}`}>
          <div className="flex items-center gap-3">
            {notification.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            <span className="font-bold">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header - Original Gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4 text-white flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Landmark className="h-6 w-6 opacity-80" />
            <div>
              <h2 className="text-xl font-bold tracking-tight">{translations[language].objectionDetails}</h2>
              <p className="text-xs text-blue-100 opacity-90">{context.villageName} | {context.talukaName} | {context.distName}</p>
            </div>
          </div>
          <button onClick={onBack} className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-8 space-y-6">
          
          {/* Row 1: Gat No */}
          <div className="grid grid-cols-12 items-center gap-6">
            <label className="col-span-4 text-right text-sm font-bold text-gray-700">
              {translations[language].SurveyNo} / {translations[language].GatNo} *
            </label>
            <div className="col-span-8 relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-blue-500" />
              <input
                type="text"
                name="gateNo"
                value={formData.gateNo}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-2 border-2 border-blue-50 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-500 outline-none transition-all font-bold text-blue-800"
                placeholder={translations[language].enterGatNo}
              />
            </div>
          </div>

          {/* Row 2: Select Owner */}
          <div className="grid grid-cols-12 items-center gap-6">
            <label className="col-span-4 text-right text-sm font-bold text-gray-700">
              {translations[language].SelectOwner} *
            </label>
            <div className="col-span-8">
              <select
                value={selectedOwnerId}
                onChange={handleOwnerSelect}
                disabled={owners.length === 0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
              >
                <option value="">{owners.length > 0 ? `-- ${owners.length} ${translations[language].ownersFound || 'Owners Found'} --` : `-- ${translations[language].enterGatNo} --`}</option>
                {owners.map(o => <option key={o.land_owner_id} value={o.land_owner_id}>{o.owner_name}</option>)}
              </select>
              {ownerMessage && <p className="text-[11px] text-red-500 mt-1 font-medium italic">{ownerMessage}</p>}
            </div>
          </div>

          {/* Row 3: Name */}
          <div className="grid grid-cols-12 items-center gap-6">
            <label className="col-span-4 text-right text-sm font-bold text-gray-700">
              {translations[language].objectorName} *
            </label>
            <div className="col-span-8">
              <input
                type="text"
                name="objectorName"
                value={formData.objectorName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 outline-none transition-all"
              />
            </div>
          </div>

          {/* Row 4: Date */}
          <div className="grid grid-cols-12 items-center gap-6">
            <label className="col-span-4 text-right text-sm font-bold text-gray-700">
              {translations[language].objectionDate} *
            </label>
            <div className="col-span-8">
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  name="objectionDate"
                  value={formData.objectionDate}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg outline-none"
                />
              </div>
            </div>
          </div>

          {/* Row 5: Address */}
          <div className="grid grid-cols-12 items-start gap-6">
            <label className="col-span-4 text-right text-sm font-bold text-gray-700 mt-2">
              {translations[language].objectorAddress}
            </label>
            <div className="col-span-8">
              <textarea
                name="objectorAddress"
                value={formData.objectorAddress}
                onChange={handleInputChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Row 6: Upload */}
          <div className="grid grid-cols-12 items-center gap-6">
            <label className="col-span-4 text-right text-sm font-bold text-gray-700">
              {translations[language].uploadDocumentss}
            </label>
            <div className="col-span-8">
              <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-lg border border-dashed border-gray-300">
                <Upload className="h-5 w-5 text-blue-600 ml-2" />
                <input
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="text-xs file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-blue-600 file:text-white"
                />
              </div>
            </div>
          </div>

          {/* Row 7: Details */}
          <div className="grid grid-cols-12 items-start gap-6">
            <label className="col-span-4 text-right text-sm font-bold text-gray-700 mt-2">
              {translations[language].objectionDetails}
            </label>
            <div className="col-span-8">
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-blue-500"
                placeholder={translations[language].enterObjectionDetails || "Details..."}
              />
            </div>
          </div>

          {/* Action Footer */}
          <div className="mt-8 flex items-center justify-end gap-4 border-t pt-6">
            <button
              onClick={onBack}
              className="px-6 py-2 text-sm font-bold text-gray-500 hover:text-gray-800 uppercase tracking-widest transition-all"
            >
              {translations[language].Cancel}
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !selectedOwnerId}
              className="px-10 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl shadow-lg hover:shadow-blue-200 flex items-center gap-2 font-bold transition-all disabled:opacity-50 disabled:grayscale"
            >
              {isLoading ? <RefreshCw className="animate-spin h-5 w-5" /> : <Save className="h-5 w-5" />}
              {translations[language].Save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}